/**
 * Entwurf im Panel „Verlauf eingrenzen“: erst „Anzeigen“ schreibt ihn in die URL. Listen werden
 * kommagetrennt, technische IDs wirken erst vollständig – halbe Eingaben bleiben im Feld stehen.
 */
import {
  EXPOSURE_KINDS,
  GUID,
  OUTCOMES,
  PUBLICATION_STATUSES,
  TRIGGERS,
  listOf,
  listValue,
  type DynamicPricingHistorySearch,
} from './historyUrlState';

type Search = DynamicPricingHistorySearch;
const SPANS = [
  'stayFrom',
  'stayTo',
  'decisionFrom',
  'decisionTo',
  'publicationCreatedFrom',
  'publicationCreatedTo',
  'exposureConfirmedFrom',
  'exposureConfirmedTo',
] as const;
const LISTS = {
  trigger: TRIGGERS,
  outcome: OUTCOMES,
  publicationStatus: PUBLICATION_STATUSES,
  exposureKind: EXPOSURE_KINDS,
} as const;

export type HistorySpanKey = (typeof SPANS)[number];
export type HistoryListKey = keyof typeof LISTS;
export type HistoryFilterDraft = Pick<Search, HistorySpanKey> &
  Record<HistoryListKey, string[]> & { configurationText: string; profileText: string };

/** Was in der URL steht, ohne Ansicht und Apartment – diese haben eigene Felder. */
export const HISTORY_FILTER_KEYS = [
  ...SPANS,
  ...(Object.keys(LISTS) as HistoryListKey[]),
  'configurationVersion',
  'profileVersion',
] as const satisfies readonly (keyof Search)[];

export const CLEARED_FILTERS: Partial<Search> = Object.fromEntries(
  HISTORY_FILTER_KEYS.map((key) => [key, undefined]),
);

export interface TechnicalTexts {
  configurationText: string;
  profileText: string;
}

export function draftOf(search: Search, texts: TechnicalTexts): HistoryFilterDraft {
  return {
    ...Object.fromEntries(SPANS.map((key) => [key, search[key]])),
    trigger: listOf(search.trigger),
    outcome: listOf(search.outcome),
    publicationStatus: listOf(search.publicationStatus),
    exposureKind: listOf(search.exposureKind),
    ...texts,
  };
}

export const emptyDraft = (): HistoryFilterDraft =>
  draftOf({}, { configurationText: '', profileText: '' });

const guid = (text: string) => (GUID.test(text.trim()) ? text.trim() : undefined);

export function applyDraft(draft: HistoryFilterDraft): Partial<Search> {
  return {
    ...Object.fromEntries(SPANS.map((key) => [key, draft[key] || undefined])),
    ...Object.fromEntries(
      (Object.keys(LISTS) as HistoryListKey[]).map((key) => [
        key,
        listValue(draft[key], LISTS[key]),
      ]),
    ),
    configurationVersion: guid(draft.configurationText),
    profileVersion: guid(draft.profileText),
  };
}

/** Eine halbe ID wirkt noch nicht; das Feld sagt es. */
export const pendingId = (text: string) => text.trim() !== '' && !guid(text);
